//
// Copyright (c) 2025 Comcast
//

import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import { useSelector } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import { useLocationQueryParams } from 'utils/hooks'

import {
  useSubscribeToTicketsViewsMutation,
  useUnsubscribeToTicketsViewsMutation,
} from 'api/subscriptions'

import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import { RouteProfile } from 'routes'
import * as sessionUserSelector from 'selectors/sessionUser'
import { useCurrentTicketingSystem } from './shared/hooks'


const SubscriptionToNewTickets = ({ initialSubscribed, onSubscribingOrUnsubscribing, providerTicketBoardId }) => {
  const [subscribed, setSubscribed] = useState(initialSubscribed)

  const { t } = useTranslation()
  const userHasValidEmail = useSelector(sessionUserSelector.isValidEmail)
  const currentTicketingSystem = useCurrentTicketingSystem()

  const ticketingSystemDisplayId = currentTicketingSystem?.displayId
  const subscribeLabel = t('ticketsPage.subscribeToTicketsView')
  const unsubscribeLabel = t('ticketsPage.unSubscribeToTicketsView')

  const [createSubscriptionToTicketsViews, { data: dataCreate, isLoading: isCreating }] = useSubscribeToTicketsViewsMutation()
  const [removeSubscriptionToTicketsViews, { data: dataRemove, isLoading: isRemoving }] = useUnsubscribeToTicketsViewsMutation()
  const [locationQueryParams, location] = useLocationQueryParams()


  const buildStoredParams = () => {
    const params = {}

    if (locationQueryParams.search) {
      params['search'] = locationQueryParams.search
    }

    if (locationQueryParams.status_filters) {
      params['status_filters'] = locationQueryParams.status_filters
    }

    if (locationQueryParams.ticket_filters || locationQueryParams.user_filters) {
      params['user_filters'] = locationQueryParams.ticket_filters || locationQueryParams.user_filters
    }

    if (providerTicketBoardId) {
      params['provider_ticket_board_id'] = providerTicketBoardId
    }

    return params
  }

  useEffect(() => {
    setSubscribed(initialSubscribed)
  }, [ticketingSystemDisplayId, initialSubscribed, location.search])

  useEffect(() => {
    onSubscribingOrUnsubscribing(isCreating)
    if (isCreating === false && dataCreate) {
      setSubscribed(dataCreate.subscribed)
    }
  }, [isCreating, dataCreate, onSubscribingOrUnsubscribing])

  useEffect(() => {
    onSubscribingOrUnsubscribing(isRemoving)
    if (isRemoving === false && dataRemove) {
      setSubscribed(dataRemove.subscribed)
    }
  }, [isRemoving, dataRemove, onSubscribingOrUnsubscribing])

  const onClick = () => {
    if (subscribed) {
      removeSubscriptionToTicketsViews({ ticketingSystemDisplayId: ticketingSystemDisplayId, storedParams: buildStoredParams() })
    } else {
      createSubscriptionToTicketsViews({ ticketingSystemDisplayId: ticketingSystemDisplayId, storedParams: buildStoredParams() })
    }
  }

  let subscribeTooltipText = null
  if (userHasValidEmail) {
    subscribeTooltipText = subscribed ? unsubscribeLabel : subscribeLabel
  }

  return (
    <>
      <div
        data-tooltip-id='subscribe-tooltip-id'
        data-tooltip-content={subscribeTooltipText}
      >
        <IconLink
          loading={false}
          active={subscribed}
          onClick={onClick}
          disabled={!userHasValidEmail}
          iconName='icon-subscribe-16'
        />
      </div>
      <Tooltip clickable id='subscribe-tooltip-id' delayHide={500}>
        <p>
          <Trans i18nKey='ticketPage.subscribeLinkHint'>
            You are not able to subscribe, <Link to={RouteProfile()}>please update your email!</Link>
          </Trans>
        </p>
      </Tooltip>
    </>
  )
}

SubscriptionToNewTickets.propTypes = {
  initialSubscribed: PropTypes.bool,
  providerTicketBoardId: PropTypes.string,
  onSubscribingOrUnsubscribing: PropTypes.func.isRequired,
}

export default SubscriptionToNewTickets
